<template>
  <div>
    <wizardModal
      modal-id="dmp-panels-wizard"
      :is-active.sync="isActive"
      :is-add="isAdd"
      :wizard-title="wizardTitle"
      :steps="wizardSteps"
      :step-icons="wizardStepIcons"
      :finishAtStep="finishAtStep"
      :current-step.sync="currentStep"
      :show-previous-button="false"
      :show-save-button="false"
      :show-next-button="false"
      :show-finish-button="currentStep === finishAtStep && !isProcessing"
      :activate-btn-save="!isAdd && hasChanges"
      :disable-next="isProcessing"
      :loading="isProcessing"
      @close-wizard="closeWizard"
      @prev-step="prevStep"
      @next-step="nextStep"
      @save-data="handleSave"
      @load-data="loadData"
      @reset-wizard="resetData"
      @on-submit="onSubmit"
      :current-step-key="'0'"
    >
      <template #step-0="currentStep, loading">
        <div class="position-relative">
          <general-step
            ref="generalStep"
            :item-data="itemData"
            :profile-options="profileOptions"
            :facilities-options="facilitiesOptions"
            :center-options="centerOptions"
            :sector-options="sectorOptions"
            :reseller-options="resellerOptions"
            :additional-routers="additionalRouters"
            @update:itemData="itemData = $event"
            @update:unit="changedUnit"
          />

          <loading-overlay
            :visible="isCreatingPanel"
            message="Creating panel..."
            variant="primary"
          />

          <loading-overlay
            :visible="isSyncingDevices"
            message="Synchronizing devices..."
            variant="success"
          />
        </div>
      </template>
    </wizardModal>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import WizardModal from "@/components/WizardModal/Index.vue";
import GeneralStep from "./GeneralStep.vue";
import LoadingOverlay from "./LoadingOverlay.vue";
import useDmpPanelsWizard from "./useDmpPanelsWizard.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useToast } from "vue-toastification/composition";

export default {
  name: "DmpPanelsWizard",
  components: {
    WizardModal,
    GeneralStep,
    LoadingOverlay,
    ToastificationContent,
  },
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    facilitiesOptions: {
      type: Array,
      required: true,
    },
    centerOptions: {
      type: Array,
      required: true,
    },
    sectorOptions: {
      type: Array,
      required: true,
    },
    resellerOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const generalStep = ref(null);

    const {
      currentStep,
      finishAtStep,
      isLoading,
      isCreatingPanel,
      isSyncingDevices,
      profileOptions,
      itemData,
      wizardTitle,
      wizardSteps,
      wizardStepIcons,
      resetData,
      closeWizard,
      prevStep,
      nextStep,
      loadData,
      handleSave,
      createItemAndSync,
      hasChanges,
      changedUnit,
      additionalRouters,
    } = useDmpPanelsWizard(props, { emit });

    const nextButtonText = computed(() => {
      if (currentStep.value === 0) {
        if (props.isAdd) {
          if (isCreatingPanel.value) {
            return "Creating...";
          }
          return "Create Panel";
        } else {
          if (hasChanges.value) {
            return "Update";
          }
          return "Close";
        }
      }
      return "Next";
    });

    const isProcessing = computed(() => {
      return isCreatingPanel.value || isSyncingDevices.value;
    });

    const onSubmit = async () => {
      if (generalStep.value && generalStep.value.refFormObserver) {
        const isValid = await generalStep.value.refFormObserver.validate();
        if (!isValid) {
          toast({
            component: ToastificationContent,
            props: {
              title: "Validation error",
              text: "Please complete all required fields",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
      }

      await createItemAndSync();
    };

    return {
      generalStep,

      currentStep,
      finishAtStep,
      isLoading,
      isCreatingPanel,
      isSyncingDevices,
      profileOptions,
      itemData,
      wizardTitle,
      wizardSteps,
      wizardStepIcons,
      hasChanges,

      nextButtonText,
      isProcessing,

      resetData,
      closeWizard,
      prevStep,
      nextStep,
      loadData,
      handleSave,
      createItemAndSync,

      onSubmit,

      toast,
      additionalRouters,
      changedUnit,
    };
  },
};
</script>
<style lang="scss" scoped></style>
